import React from 'react';
import { t } from '@citrite/translate';
import { ClockIcon, layout, palette, typography } from '@citrite/web-ui-component';
import styled from '@emotion/styled';
import moment from 'moment';
import { detectLanguage } from 'javascript/sf/detectLanguage';

const AllActiveLeases = require('images/success-art.svg');

const StyledContainer = styled.div`
	display: flex;
	padding: ${layout.mediumSpace};
	flex-direction: column;
	justify-content: center;
	align-items: center;
	gap: ${layout.mediumLargeSpace};
	align-self: stretch;

	border-radius: ${layout.smallSpace};
	border: 1px solid var(--Greyscale-Grey-200, ${palette.grey200});
	background: var(--Greyscale-White, ${palette.white});
`;

const StyledTitleText = styled.span`
	font-weight: ${typography.weights.regular};
	font-size: ${typography.sizes.xl};
	color: ${palette.black};
`;

const StyledInfoContainer = styled.div`
	display: flex;
	align-items: center;
	flex-direction: column;
	gap: ${layout.space16};
`;

const StyledSubText = styled.span`
	font-weight: ${typography.weights.regular};
	font-size: ${typography.sizes.sml2};
	text-align: center;
	color: ${palette.black};
`;

const StyledInfoText = styled.span<{ fontSize?: 'Large' | 'Small' }>`
	font-weight: ${typography.weights.regular};
	font-size: ${props =>
		props.fontSize === 'Large' ? typography.sizes.sml : typography.sizes.xsml};
	color: ${palette.grey700};
	padding: 0 ${layout.space4};
`;

const StyledInfoSection = styled.div`
	display: flex;
	align-items: flex-start;
`;

const StyledClockIcon = styled(ClockIcon)`
	display: flex;
`;

const formatTimestamp = (timestamp: string): string => {
	const momentTime = moment(parseInt(timestamp, 10));
	const now = moment();

	// Check if the date is today
	const isToday = momentTime.isSame(now, 'day');

	if (isToday) {
		// Today: just show time in hh:mm AM/PM format
		return momentTime.locale(detectLanguage()).format('h:mm A');
	} else {
		// Any other day (past or future): show MM/DD/YYYY hh:mm AM/PM format
		return momentTime.locale(detectLanguage()).format('dddd, MMMM DD, YYYY h:mm A');
	}
};

export const LeasesLastSyncIndicator = ({
	lastSyncTime,
	fontSize,
}: {
	lastSyncTime: string;
	fontSize?: 'Large' | 'Small';
}) => {
	return (
		!!lastSyncTime && (
			<StyledInfoSection>
				<StyledClockIcon size={fontSize === 'Large' ? 14 : 12} color={palette.grey700} />
				<StyledInfoText fontSize={fontSize}>
					{t('Workspace:service_continuity.last_sync_time', {
						time: formatTimestamp(lastSyncTime),
					})}
				</StyledInfoText>
			</StyledInfoSection>
		)
	);
};

export interface ActiveLeasesStateProps {
	activeLeasesCount: number;
	lastSyncTime: string;
}

export function ActiveLeasesState({
	activeLeasesCount,
	lastSyncTime,
}: ActiveLeasesStateProps) {
	return (
		<StyledContainer>
			<img
				src={AllActiveLeases}
				alt={t('Workspace:service_continuity.active_leases_alt_text', {
					count: activeLeasesCount,
				})}
			/>
			<StyledInfoContainer>
				<StyledTitleText>
					{activeLeasesCount === 1
						? t('Workspace:service_continuity.active_leases_title_one')
						: t('Workspace:service_continuity.active_leases_title_other', {
								count: activeLeasesCount,
						  })}
				</StyledTitleText>
				<StyledSubText>
					{t('Workspace:service_continuity.active_leases_description')}
				</StyledSubText>
			</StyledInfoContainer>
			<LeasesLastSyncIndicator lastSyncTime={lastSyncTime} />
		</StyledContainer>
	);
}
